import Vue from 'vue'
import Vuex from 'vuex'
import uuid4 from 'uuid4'
import filterBL from './bl/filterBl'

Vue.use(Vuex)

const store = new Vuex.Store({
  state: {
    // User

    user: {},
    baseUrl: process.env.NODE_ENV === 'production'
      ? 'https://insitu.de'
      : 'http://localhost:8070',

    // Data
    currentCandidateFilter: 'new',
    uploadCount: 0,
    wartelisteCount: 0,
    inboxCount: 0,
    abgesagtCount: 0,
    eingestelltCount: 0,
    verifiedCount: 0,
    allCount: 0,

    filterQuestions: {},
    filterAnswers: {},

    candidateUUID: undefined,
    metaInfos: {},
    candidates: [],
    questions: [],
    pages: [],
    categories: [],
    dsb: {
      version: void 0,
      html: void 0
    },

    // Permissions

    permissions: {
      read_candidate: false,
      edit_candidate: false,
      verify_candidate: false,
      delete_candidate: false,
      finalize_candidate: false,
      promote_candidate: false,
      delete_file: false,
      get_file: false
    }
  },

  getters: {
    // User

    getUser: (state) => state.user,
    getBaseUrl: state => state.baseUrl,

    // Data
    getDisplayedCandidates: (state) => {
      return filterBL.filterCandidates(state.candidates, state.filterAnswers, state.questions)
    },

    // Filter
    filterQuestions: state => state.filterQuestions,
    getFilterAnswer: state => key => state.filterAnswers[key],
    hasActiveFilter: state => Object.keys(state.filterAnswers).length > 0,

    hasRunningUploads: state => state.uploadCount > 0,
    getCandidateUUID: state => state.candidateUUID,
    getCandidates: (state) => state.candidates
      .filter(candidate => candidate.metadata.status === state.currentCandidateFilter),
    getQuestions: (state) => state.questions,
    getPages: (state) => state.pages,
    getCategories: (state) => state.categories,
    getDSB: (state) => state.dsb.html,
    getDSBVersion: (state) => state.dsb.version,
    getWartelisteCount: state => state.wartelisteCount,
    getInboxCount: state => state.inboxCount,
    getEingestelltCount: state => state.eingestelltCount,
    getAbgesagtCount: state => state.abgesagtCount,
    getVerifiedCount: state => state.verifiedCount,
    getAllCount: state => state.allCount,
    getCurrentCandidateFilter: state => state.currentCandidateFilter,
    getMetaInfoForCandidate: state => uuid => state.metaInfos[uuid],

    // Permissions

    hasPermissionToViewCandidatesList: (state) => state.permissions.edit_candidate || state.permissions.read_candidate,
    hasPermissionToCreateCandidate: () => true,
    hasPermissionToViewCandidate: state => state.permissions.read_candidate,
    hasPermissionToEditCandidate: (state) => state.permissions.edit_candidate,
    hasPermissionToVerifyCandidate: (state) => state.permissions.verify_candidate,
    hasPermissionToDeleteCandidate: (state) => state.permissions.delete_candidate,
    hasPermissionToFinalizeCandidate: (state) => state.permissions.finalize_candidate,
    hasPermissionToPromoteCandidate: (state) => state.permissions.finalize_candidate,
    hasPermissionToDeleteFile: state => state.permissions.delete_file,
    hasPermissionToGetFile: state => state.permissions.get_file,
    readOnlyMode: state =>
      state.permissions.read_candidate &&
      !state.permissions.edit_candidate &&
      !state.permissions.verify_candidate &&
      !state.permissions.finalize_candidate &&
      !state.permissions.delete_candidate,
    getPermissions: (state) => state.permissions
  },

  mutations: {
    // FilterAnswers
    setFilterAnswer (state, payload) {
      Vue.set(state.filterAnswers, payload.questionKey, payload.value)
    },
    addFilterAnswer (state, payload) {
      const answer = state.filterAnswers[payload.questionKey] || []
      answer.push(payload.value)
      Vue.set(state.filterAnswers, payload.questionKey, answer)
    },
    clearFilterAnswers (state) {
      Vue.set(state, 'filterAnswers', {})
    },
    // User

    setUser (state, payload) {
      state.user = payload.user
    },

    setBaseUrl (state, baseUrl) {
      if (!baseUrl) {
        return
      }
      // remove Slash if on last position
      if (baseUrl.charAt(baseUrl.length - 1) === '/') {
        baseUrl = baseUrl.substring(0, baseUrl.length - 1)
      }
      state.baseUrl = baseUrl
    },

    setMetaInfos (state, metaInfos) {
      Vue.set(state, 'metaInfos', metaInfos)
    },

    setCurrentCandidateFilter (state, filter) {
      state.currentCandidateFilter = filter
    },

    addRunningUpload (state) {
      state.uploadCount = state.uploadCount + 1
    },

    removeRunningUpload (state) {
      state.uploadCount = state.uploadCount - 1
    },

    generateCandidateUUID (state) {
      if (!state.candidateUUID) {
        state.candidateUUID = uuid4()
      }
    },

    clearCandidateUUID (state) {
      state.candidateUUID = undefined
    },

    setCandidateUUID (state, candidateUUID) {
      state.candidateUUID = candidateUUID
    },

    addCandidate (state, payload) {
      state.candidates.push(payload.candidate)
    },

    setCandidates (state, payload) {
      state.candidates = payload.candidates || []
    },

    setCandidateCount (state, payload) {
      state.inboxCount = payload.inboxCount || 0
      state.wartelisteCount = payload.wartelisteCount || 0
      state.eingestelltCount = payload.eingestelltCount || 0
      state.abgesagtCount = payload.abgesagtCount || 0
      state.verifiedCount = payload.verifiedCount || 0
      state.allCount = payload.allCount || 0
    },

    setCandidateDSB (state, payload) {
      const candidate = payload.candidate

      if (!candidate) {
        return
      }

      const target = state.candidates.find(c => c.uuid === candidate.uuid)

      if (!target) {
        return
      }

      if (!target.metadata) {
        Vue.set(target, 'metadata', {})
      }

      Vue.set(target.metadata, 'dsb_accepted_version', payload.value)
    },

    setQuestions (state, payload) {
      Vue.set(state, 'questions', payload.questions)
      const filterQuestions = filterBL.computeFilterQuestions(payload.questions)
      Vue.set(state, 'filterQuestions', filterQuestions)
    },

    setPages (state, payload) {
      state.pages = payload.pages
    },

    setCategories (state, payload) {
      state.categories = payload.categories
    },

    setDSBVersion (state, payload) {
      state.dsb.version = payload.dsb_version
    },

    setDSB (state, payload) {
      state.dsb.html = payload.dsb
    },

    updateAnswersForCandidate (state, payload) {
      const candidate = state.candidates.find(c => c.uuid === payload.candidate.uuid)

      if (candidate) {
        Vue.set(candidate, 'answers', payload.answers)
      }
    },

    updateStatusForCandidate (state, payload) {
      const candidate = state.candidates.find(c => c.uuid === payload.candidate.uuid)

      if (candidate) {
        Vue.set(candidate.metadata, 'status', payload.status.value)
        Vue.set(candidate.metadata, 'activated', payload.activated)
      }
    },

    updateCandidateMetadata (state, candidate) {
      const candidateInStore = state.candidates.find(c => c.uuid === candidate.uuid)

      if (candidateInStore) {
        Vue.set(candidateInStore, 'metadata', candidate.metadata)
      }
    },

    // Permissions

    setPermissions (state, payload) {
      state.permissions = payload.permissions
    }
  }
})

window.$store = store
export default store
